import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
} from '@mui/x-data-grid';
import {
  randomId,
} from '@mui/x-data-grid-generator';
import { Chapter, Choice, ImageUploader, MultipleSelect, showImage } from '../../../Components/Medias';
import { ResponseProps } from './types';
import { ChapterProps } from '../../Chapters/components/types';
import Swal from 'sweetalert2';
import { successToast } from '../../../Common/Helpers/Utils';
import { CircularProgress } from '@mui/material';
import Toast from '../../../Common/Helpers/Toast';
import { useDispatch, useSelector } from 'react-redux';
import ChaptersOperations from '../../../InfraStructure/Chapters/Operations';
import { AppDispatch } from '../../../InfraStructure/store';
import { CustomGridToolbarProps, LevelProps } from '../../../@types/types';
import LevelsOperations from '../../../InfraStructure/Levels/LevelsOperations';

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
  ) => void;
}

type DataProps = {
  data: GridRowsProp;
  onSave: (data: any) => Promise<ResponseProps>;
  onUpdate: (data: any) => Promise<ResponseProps>;
  onDelete: (id: GridRowId) => Promise<boolean>;
};

export default function CrudVideos({ data, onSave, onUpdate, onDelete }: DataProps) {
  const [rows, setRows] = React.useState(data);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [mode, setMode] = React.useState<'view' | 'edit' | 'new'>('view');
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [loading, setLoading] = React.useState({ id: undefined, loading: false });
  const [levels, setLevels] = React.useState<LevelProps[]>([]);
  const [chapters, setChapters] = React.useState<ChapterProps[]>([]);
  // eslint-disable-next-line
  const [chapter, setChapter] = React.useState({ id: 0, nome: '' });
  // eslint-disable-next-line
  const [active, setActive] = React.useState<number | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const {
    Data: DataChapter,
    // IsLoading: IsLoadingChapter,
    // HasError: HasErrorChapter,
  } = useSelector((state: any) => state.chapters);
  const {
    Data: DataLevels,
    // IsLoading: IsLoadingLevels,
    // HasError: HasErrorLevels,
  } = useSelector((state: any) => state.levels);

  function EditToolbar(props: EditToolbarProps) {
    const { setRows, setRowModesModel } = props;
  
    const handleClick = () => {
      const id = randomId();
      setRows((oldRows) => [
        ...oldRows, {
          id,
          nome: '',
          capitulo: '',
          imagem: '',
          nivel_id: undefined,
          ativo: true,
          created_at: new Date().toLocaleDateString(),
          isNew: true }]);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: 'nome' },
      }));

      setMode('new');
    };
  
    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={mode !== 'view'}
          onClick={handleClick}>
          Adicionar
        </Button>
      </GridToolbarContainer>
    );
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.escapeKeyDown) {
      setRowModesModel({
        ...rowModesModel,
        [params.id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
      setMode('view');
    }
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleValidationData = (data: any) => {
    if (!data.nome) return { isValid: false, message: 'Nome é obrigatório.' };
    if (!data.capitulo_id) return { isValid: false, message: 'Capítulo é obrigatório.' };
    if (!data.nivel_id) return { isValid: false, message: 'Nível é obrigatório.' };
    if (!data.imagem) return { isValid: false, message: 'Imagem é obrigatória.' };
    if (!data.url) return { isValid: false, message: 'Url do vídeo é obrigatória.' };
    if (data.ativo === null) return { isValid: false, message: 'Ativo é obrigatório.' };

    return { isValid: true, message: '' };
  }

  const handleEditClick = (id: GridRowId) => () => {
    if (mode === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);
      return;
    }
    setMode('edit');
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    Swal.fire({
      title: 'Atenção',
      text: "Deseja mesmo apagar o Vídeo?",
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, apagar!',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading({ id, loading: true });
        const response = await onDelete(id);
        if (response === true) {
          setRows(rows.filter((row) => row.id !== id));
          successToast('Vídeo apagado com sucesso!');
          setLoading({ id, loading: false });
        } else {
          setOpen(true);
          setMessage('Erro ao apagar o Vídeo.');
          setLoading({ id, loading: false });
        }
      }
    });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setMode('view');
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleLevels = async () => {
    await dispatch(LevelsOperations.getLevels());
  }

  const handleChapters = async () => {
    dispatch(ChaptersOperations.getChapters());
  }

  React.useEffect(() => {
    if (DataChapter && DataChapter.length > 0) {
      setChapters(DataChapter);
    }
    if (DataLevels && DataLevels.length > 0) {
      setLevels(DataLevels);
    }
  }, [DataChapter, DataLevels]);

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow = {
      ...newRow,
      id: newRow.id,
      isNew: false,
      imagem: typeof newRow.imagem === 'object' ? newRow.imagem : '',
      capitulo: newRow.capitulo,
      capitulo_id: newRow.capitulo_id,
    }

    if (mode === 'new') {
      const validation = handleValidationData(updatedRow);

      if (!validation.isValid) {
        setOpen(true);
        setMessage(validation.message);
        return null;
      }

      setLoading({ id: newRow.id, loading: true });
      
      const response: ResponseProps = await onSave(updatedRow);

      if (response.status === 'success') {
        setMode('view');
        successToast(response.message);

        const returnedRow = {
          ...updatedRow,
          nivel: response.data.nivel,
          nivel_id: newRow.nivel_id,
          imagem: response.data.imagem,
          capitulo: response.data.capitulo,
          capitulo_id: response.data.capitulo_id,
          criado_em: response.data.criado_em,
        }
        
        setRows(rows.map((row) => (row.id === newRow.id ? returnedRow : row)));
        setLoading({ id: newRow.id, loading: false });
        return returnedRow;
      } else {
        setOpen(true);
        setMessage(response.message);
        setLoading({ id: newRow.id, loading: false });
      }
    } else {
      setLoading({ id: newRow.id, loading: true });

      const response: ResponseProps = await onUpdate(updatedRow);

      if (response.status === 'success') {
        setMode('view');
        successToast(response.message);

        const returnedRow = {
          ...updatedRow,
          nivel: response.data.nivel,
          nivel_id: newRow.nivel_id,
          imagem: response.data.imagem,
          capitulo: response.data.capitulo,
          capitulo_id: response.data.capitulo_id,
          ativo: Number(response.data.ativo),
        };

        setRows(rows.map((row) => (row.id === newRow.id ? returnedRow : row)));
        setLoading({ id: newRow.id, loading: false });
        return returnedRow;
      } else {
        setOpen(true);
        setMessage(response.message);
        setLoading({ id: newRow.id, loading: false });
      }
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleVerifyEditMode = (params: any) => {
    if (mode === 'edit') {
      setMessage('Não é possível editar mais de um registro por vez.');
      setOpen(true);
      
      handleCancelClick(params.id)();
      setMode('edit');
      return;
    } else {
      setMode('edit');
    }
  }

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  React.useEffect(() => {
    handleLevels();
    handleChapters();
    // eslint-disable-next-line
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'nome',
      minWidth: 180,
      flex: 1,
      editable: true,
      type: 'string',
      headerAlign: 'left',
      renderHeader: () => <strong>Nome</strong>,
    },
    {
      field: 'capitulo_id',
      minWidth: 150,
      flex: 1,
      editable: true,
      type: 'string',
      headerAlign: 'left',
      renderHeader: () => <strong>Capítulo</strong>,
      renderCell: (params) => {
        return chapters.find((item: ChapterProps) => item.id === Number(params.value))?.nome;
      },
      renderEditCell: (params) => {
        const data = {
          ...params,
          chapters,
        }
        return <Chapter params={data} setChapter={setChapter} />;
      },
    },
    {
      field: 'nivel_id',
      minWidth: 150,
      flex: 1,
      editable: true,
      type: 'string',
      headerAlign: 'center',
      renderHeader: () => <strong>Curso</strong>,
      renderCell: (params) => {
        return levels.find((item: LevelProps) => item.id === Number(params.value[0]))?.nivel;
      },
      renderEditCell(params) {
        const fieldParams = {
          ...params,
          items: levels,
          column: 'nivel_id',
          nome: 'nivel',
        }

        return <MultipleSelect params={fieldParams} />
      },
    },
    {
      field: 'imagem',
      type: 'string',
      minWidth: 70,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      editable: true,
      renderHeader: () => <strong>Imagem</strong>,
      renderCell: (params) => (
        <img
          src={params.value as string}
          alt={params.value as string}
          style={{ height: 46, padding: 2, cursor: 'pointer' }}
          onClick={() => showImage(params.value as string)}
        />
      ),
      renderEditCell: (params) => {
        const data = {
          ...params,
          height: 50,
        }
        return <ImageUploader params={data} />
      },
    },
    {
      field: 'ativo',
      type: 'string',
      minWidth: 70,
      flex: 1,
      editable: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <strong>Ativo</strong>,
      renderCell: (params) => (
        params.value ? 'Sim' : 'Não'
      ),
      renderEditCell: (params) => {
        const data = {
          ...params,
          height: 50,
        }
        return <Choice params={data} setActive={setActive} />
      },
    },
    {
      field: 'url',
      headerAlign: 'center',
      type: 'string',
      minWidth: 180,
      flex: 1,
      editable: true,
      renderHeader: () => <strong>URL do Vídeo</strong>,
    },
    {
      field: 'criado_em',
      headerAlign: 'center',
      type: 'string',
      minWidth: 180,
      flex: 1,
      editable: false,
      renderHeader: () => <strong>Criado em</strong>,
    },
    {
      field: 'actions',
      type: 'actions',
      renderHeader: () => <strong>Ações</strong>,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={(loading.id === id && loading.loading) ? <CircularProgress size={16} /> : <SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
              disabled={loading.loading}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              disabled={loading.loading}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={(loading.id === id && loading.loading) ? <CircularProgress size={16} /> : <EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            disabled={loading.loading}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            disabled={loading.loading}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        '& .actions': {
          color: 'text.secondary',
        },
        '& .textPrimary': {
          color: 'text.primary',
        },
      }}
    >
      <Toast
        message={message}
        open={open}
        setclose={setOpen}
        severity="error"
      />
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        disableColumnMenu
        onRowDoubleClick={handleVerifyEditMode}
        columnVisibilityModel={{
          nivel: (mode === 'edit' || mode === 'new'),
          url: (mode === 'edit' || mode === 'new'),
          criado_em: mode === 'view',
        }}
        autosizeOptions={{
          columns: [
            'nome',
            'capitulo',
            'nivel',
            'imagem',
            'url',
            'ativo',
            'criado_em',
          ],
          includeOutliers: true,
          includeHeaders: true,
        }}
        localeText={{
          noRowsLabel: 'Nenhum vídeo encontrado.',
          footerRowSelected: (count) => `${count} vídeo(s) selecionado(s)`,
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: EditToolbar as unknown as GridSlots['toolbar'],
        }}
        slotProps={{
          toolbar: { setRows, setRowModesModel } as CustomGridToolbarProps,
          pagination: {
            labelRowsPerPage: "Vídeos por página",
            labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
          }
        }}
      />
    </Box>
  );
}